import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Shirt, Search, Loader2, Undo2, PackageCheck, Check, X } from "lucide-react";
import { AuthImage } from "../components/AuthImage";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, ConfirmDialog, Field, PeralatanBadge, PeralatanMeta } from "../components/common";
import { AnakCombobox, RupiahInput } from "../components/inputs";
import { api, errMsg } from "../lib/api";
import { fmtDate, rp, todayISO } from "../lib/format";

const KONDISI = [["baik", "Baik"], ["rusak", "Rusak"], ["hilang", "Hilang"]];
const STATUS = [["perlu_cek", "Perlu Dicek"], ["dipinjam", "Dipinjam"], ["dikembalikan", "Dikembalikan"], ["", "Semua"]];

const BiayaFields = ({ f, set }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    <Field label="Biaya (opsional)" hint="Catatan saja — tidak memotong gaji"><RupiahInput value={f.biaya} onChange={(v) => set("biaya", v)} testid="peralatan-biaya-input" placeholder="0" /></Field>
    <Field label="Keterangan biaya"><Input data-testid="peralatan-ket-biaya-input" className="h-10" placeholder="sewa / rusak / hilang" value={f.keterangan_biaya} onChange={(e) => set("keterangan_biaya", e.target.value)} /></Field>
  </div>
);

const FormDialog = ({ open, onOpenChange, initial, anak, onSaved }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({ anak_id: initial?.anak_id || "", nama_barang: initial?.nama_barang || "", jumlah: String(initial?.jumlah || 1), tanggal_pinjam: initial?.tanggal_pinjam || todayISO(),
      catatan: initial?.catatan || "", biaya: initial?.biaya ? String(initial.biaya) : "", keterangan_biaya: initial?.keterangan_biaya || "" });
  }, [open, initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.anak_id) return toast.error("Pilih anak terlebih dahulu");
    if (!f.nama_barang.trim()) return toast.error("Nama barang wajib diisi");
    const jumlah = parseInt(f.jumlah, 10);
    if (!(jumlah >= 1)) return toast.error("Jumlah minimal 1");
    setSaving(true);
    try {
      const body = { anak_id: f.anak_id, nama_barang: f.nama_barang.trim(), jumlah, tanggal_pinjam: f.tanggal_pinjam, catatan: f.catatan.trim() || null,
        biaya: f.biaya ? Number(f.biaya) : null, keterangan_biaya: f.keterangan_biaya.trim() || null };
      if (initial) await api.put(`/peralatan/${initial.id}`, body);
      else await api.post("/peralatan", body);
      toast.success(initial ? "Data peralatan diperbarui" : "Peminjaman dicatat");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="peralatan-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah peminjaman" : "Catat peminjaman"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Anak"><AnakCombobox anak={anak.filter((a) => a.aktif || a.id === f.anak_id)} value={f.anak_id} onChange={(v) => set("anak_id", v)} testid="peralatan-anak" /></Field>
          <div className="grid grid-cols-[1fr_96px] gap-3">
            <Field label="Nama barang"><Input data-testid="peralatan-nama-input" className="h-10" placeholder="mis. Dress merah" value={f.nama_barang || ""} onChange={(e) => set("nama_barang", e.target.value)} /></Field>
            <Field label="Jumlah"><Input data-testid="peralatan-jumlah-input" inputMode="numeric" className="h-10 text-center font-mono" value={f.jumlah || ""} onChange={(e) => set("jumlah", e.target.value.replace(/\D/g, "").slice(0, 3))} /></Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tanggal pinjam"><Input type="date" data-testid="peralatan-tanggal-input" className="h-10" value={f.tanggal_pinjam || ""} onChange={(e) => set("tanggal_pinjam", e.target.value)} /></Field>
            <Field label="Catatan (opsional)"><Input data-testid="peralatan-catatan-input" className="h-10" value={f.catatan || ""} onChange={(e) => set("catatan", e.target.value)} /></Field>
          </div>
          <BiayaFields f={f} set={set} />
          <DialogFooter><Button data-testid="peralatan-save-btn" type="submit" disabled={saving} className="h-10 w-full rounded-full sm:w-auto">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const KembaliDialog = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (item) setF({ tanggal_kembali: todayISO(), kondisi: "baik", biaya: item.biaya ? String(item.biaya) : "", keterangan_biaya: item.keterangan_biaya || "" }); }, [item]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  if (!item) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/peralatan/${item.id}/kembali`, { tanggal_kembali: f.tanggal_kembali, kondisi: f.kondisi || null, biaya: f.biaya ? Number(f.biaya) : null, keterangan_biaya: f.keterangan_biaya.trim() || null });
      toast.success(`${item.nama_barang} ditandai dikembalikan`);
      onClose();
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="kembali-dialog">
        <DialogHeader><DialogTitle className="font-heading">Kembalikan: {item.nama_barang} · {item.anak_nama}</DialogTitle></DialogHeader>
        <Field label="Tanggal kembali"><Input type="date" data-testid="kembali-tanggal-input" className="h-10" value={f.tanggal_kembali || ""} onChange={(e) => set("tanggal_kembali", e.target.value)} /></Field>
        <Field label="Kondisi">
          <div className="grid grid-cols-3 gap-2">
            {KONDISI.map(([v, l]) => (
              <button key={v} type="button" data-testid={`kembali-kondisi-${v}`} onClick={() => set("kondisi", v)}
                className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${f.kondisi === v ? "border-primary bg-primary text-white" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <BiayaFields f={f} set={set} />
        <DialogFooter><Button data-testid="kembali-save-btn" disabled={saving || !f.tanggal_kembali} onClick={submit} className="h-10 w-full rounded-full sm:w-auto">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Tandai Dikembalikan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TolakDialog = ({ item, onClose, onSaved }) => {
  const [alasan, setAlasan] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setAlasan(""), [item]);
  if (!item) return null;
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/peralatan/${item.id}/tolak`, { alasan: alasan.trim() || null }); toast.success("Ditolak"); onClose(); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tolak-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tolak {item.cek_jenis === "kembali" ? "laporan kembali" : "pinjaman"}: {item.nama_barang}</DialogTitle></DialogHeader>
        <Field label="Alasan (opsional, terlihat oleh anak)"><Input data-testid="tolak-alasan-input" className="h-10" value={alasan} onChange={(e) => setAlasan(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="tolak-save-btn" disabled={saving} onClick={submit} className="h-10 w-full rounded-full bg-red-600 hover:bg-red-700 sm:w-auto">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Tolak</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function PeralatanPage() {
  const [anak, setAnak] = useState([]);
  const [status, setStatus] = useState("dipinjam");
  const [fAnak, setFAnak] = useState("");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ open: false, item: null });
  const [kembali, setKembali] = useState(null);
  const [del, setDel] = useState(null);
  const [tolak, setTolak] = useState(null);

  useEffect(() => { api.get("/anak").then((r) => setAnak(r.data)); }, []);
  const load = useCallback(() => {
    api.get("/peralatan", { params: { status: status && status !== "perlu_cek" ? status : undefined, perlu_cek: status === "perlu_cek" || undefined, anak_id: fAnak || undefined, q: q || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status, fAnak, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader title="Peralatan" testid="peralatan-title" subtitle="Catatan pinjam barang (bukan stok). Biaya hanya catatan, tidak memotong gaji."
        actions={<Button data-testid="peralatan-add-btn" className="h-10 rounded-full" onClick={() => setForm({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" />Catat pinjam</Button>} />
      <div data-testid="peralatan-summary" className="mb-5 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <Shirt className="h-5 w-5 text-amber-700" />
        <p className="text-sm text-amber-900"><b data-testid="peralatan-summary-count" className="font-mono text-lg">{d?.jumlah_dipinjam ?? 0}</b> barang sedang dipinjam</p>
      </div>
      <div className="mb-4 grid gap-3 md:grid-cols-[auto_1fr_1fr]">
        <div className="flex overflow-x-auto rounded-lg border border-gray-200 bg-white p-1">
          {STATUS.map(([v, l]) => (
            <button key={v || "all"} data-testid={`peralatan-filter-${v || "all"}`} onClick={() => setStatus(v)}
              className={`h-10 flex-1 whitespace-nowrap rounded-md px-3 text-sm font-semibold transition-colors ${status === v ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              {l}{v === "perlu_cek" && d?.perlu_cek > 0 && <span data-testid="peralatan-perlu-cek-count" className="ml-1.5 rounded-full bg-sky-600 px-1.5 py-0.5 font-mono text-[10px] text-white">{d.perlu_cek}</span>}
            </button>
          ))}
        </div>
        <AnakCombobox anak={anak} value={fAnak} onChange={setFAnak} allowAll testid="peralatan-filter-anak" />
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input data-testid="peralatan-search" className="h-10 bg-white pl-10" placeholder="Cari nama barang…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {d && d.items.length === 0 ? <EmptyState icon={Shirt} title="Tidak ada data" desc="Belum ada peminjaman pada filter ini." /> : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {(d?.items || []).map((p) => (
            <div key={p.id} data-testid={`peralatan-row-${p.id}`} className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
              {p.has_foto && <AuthImage src={`/peralatan/${p.id}/foto`} className="h-10 w-12 shrink-0 rounded-lg" testid={`peralatan-foto-${p.id}`} />}
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-gray-900">{p.nama_barang}{p.jumlah > 1 && <span className="font-mono text-gray-500"> ×{p.jumlah}</span>}</p>
                <p className="break-words text-[13px] text-gray-500">{p.anak_nama} · pinjam {fmtDate(p.tanggal_pinjam)}{p.tanggal_kembali ? ` · kembali ${fmtDate(p.tanggal_kembali)}` : ""}{p.kondisi ? ` · ${p.kondisi}` : ""}</p>
                {(p.biaya || p.catatan) && <p className="break-words text-[13px] text-gray-500">{p.biaya ? <span className="font-mono text-gray-700">{rp(p.biaya)}{p.keterangan_biaya ? ` (${p.keterangan_biaya})` : ""}</span> : null}{p.biaya && p.catatan ? " · " : ""}{p.catatan}</p>}
                {p.cek_jenis === "kembali" && p.laporan_kembali && <p className="text-xs font-medium text-sky-700">Anak lapor kembali {fmtDate(p.laporan_kembali.tanggal_kembali)} · kondisi {p.laporan_kembali.kondisi}</p>}
                {p.alasan_tolak && <p className="text-xs text-red-600">{p.alasan_tolak}</p>}
                <PeralatanMeta p={p} />
              </div>
              <PeralatanBadge status={p.status_tampil} testid={`peralatan-status-${p.id}`} />
              <div className="flex shrink-0">
                {p.verifikasi === "menunggu" ? (
                  <>
                    <Button data-testid={`peralatan-konfirmasi-${p.id}`} className="h-9 rounded-full px-3" onClick={() => act(() => api.post(`/peralatan/${p.id}/konfirmasi`), "Dikonfirmasi")}><Check className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Konfirmasi</span></Button>
                    <Button data-testid={`peralatan-tolak-${p.id}`} variant="ghost" className="h-9 px-3 text-red-600" onClick={() => setTolak(p)}><X className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Tolak</span></Button>
                  </>
                ) : p.status_tampil === "ditolak" ? null : p.status === "dipinjam" ? (
                  <Button data-testid={`peralatan-kembali-${p.id}`} variant="outline" className="h-9 rounded-full px-3" onClick={() => setKembali(p)}><PackageCheck className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Tandai Dikembalikan</span></Button>
                ) : (
                  <Button data-testid={`peralatan-batal-kembali-${p.id}`} variant="ghost" size="icon" className="h-9 w-9" title="Batalkan pengembalian" onClick={() => act(() => api.post(`/peralatan/${p.id}/batal-kembali`), "Status kembali ke Dipinjam")}><Undo2 className="h-4 w-4" /></Button>
                )}
                <Button data-testid={`peralatan-edit-${p.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => setForm({ open: true, item: p })} aria-label="Ubah"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`peralatan-delete-${p.id}`} variant="ghost" size="icon" className="h-9 w-9 text-red-600 hover:bg-red-50" onClick={() => setDel(p)} aria-label="Hapus"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={form.open} initial={form.item} anak={anak} onOpenChange={(o) => setForm((s) => ({ ...s, open: o }))} onSaved={load} />
      <TolakDialog item={tolak} onClose={() => setTolak(null)} onSaved={load} />
      <KembaliDialog item={kembali} onClose={() => setKembali(null)} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title="Hapus catatan peminjaman?" description={del ? `${del.nama_barang} — ${del.anak_nama}` : ""}
        confirmLabel="Hapus" onConfirm={() => { act(() => api.delete(`/peralatan/${del.id}`), "Catatan dihapus"); setDel(null); }} testid="peralatan-delete-confirm" />
    </div>
  );
}
